import { isEmpty } from 'lodash'

export const getHorizontalGroupedSeries = ({ series, otherConfig }) => {
  const { color, yKey, legendLabels, disabledAnimations } = otherConfig

  return series.map((serie, i) => {
    const config = {
      type: 'ColumnSeries',
      name: isEmpty(legendLabels) ? serie : legendLabels[serie],
      fill: color?.fixedColor || (color && color[serie]),
      columns: {
        template: {
          type: 'Column',
          strokeOpacity: 0,
          tooltipText: '{name} : {valueX}'
        }
      },
      stacked: false,
      dataFields: {
        valueX: serie,
        categoryY: yKey
      },
      hiddenState: { transitionDuration: 200 },
      sequencedInterpolation: true,
      sequencedInterpolationDelay: 100
    }

    if (disabledAnimations) {
      delete config.hiddenState
    }

    return config
  })
}
export const getHorizontalStackedSeries = ({ series, otherConfig }) => {
  const { color, yKey, legendLabels, disabledAnimations } = otherConfig

  return series.map((serie, i) => {
    const config = {
      type: 'ColumnSeries',
      name: isEmpty(legendLabels) ? serie : legendLabels[serie],
      fill: color?.fixedColor || (color && color[serie]),
      columns: {
        template: {
          type: 'Column',
          strokeOpacity: 0,
          tooltipText: '{name} : {valueX}'
        }
      },
      stacked: true,
      dataFields: {
        valueX: serie,
        categoryY: yKey
      },
      hiddenState: { transitionDuration: 200 },
      sequencedInterpolation: true,
      sequencedInterpolationDelay: 100
    }

    if (disabledAnimations) {
      delete config.hiddenState
    }

    return config
  })
}
const getStackedSeries = ({ series, otherConfig }) => {
  const { color, sync, xKey, legendLabels, disabledAnimations } = otherConfig

  return series.map((serie, i) => {
    const config = {
      name: isEmpty(legendLabels) ? serie : legendLabels[serie],
      fill: color && color[serie],
      type: 'ColumnSeries',
      stacked: true,
      yAxis: !sync ? 'CommonYAxis' : `ColumnSeries`,
      dataFields: {
        valueY: serie,
        categoryX: xKey
      },
      hiddenState: {
        transitionDuration: 500
      },
      defaultState: {
        transitionDuration: 500
      },
      columns: {
        template: {
          width: '100%',
          type: 'Column',
          strokeOpacity: 0
        }
      }
    }

    if (disabledAnimations) {
      delete config.hiddenState
      delete config.defaultState
    }

    return config
  })
}
const getPieSeries = ({ series, otherConfig }) => {
  const {
    xKey,
    legendLabel,
    color,
    centerTitle,
    centerTitleSize,
    disabledAnimations
  } = otherConfig
  const bulletsContainer = !isEmpty(centerTitle)
    ? {
        children: [
          {
            type: 'Label',
            forceCreate: true,
            text: centerTitle,
            horizontalCenter: 'middle',
            verticalCenter: 'middle',
            fontSize: centerTitleSize || 12
          }
        ]
      }
    : null

  const config = {
    ticks: { disabled: true },
    name: isEmpty(legendLabel) ? series : legendLabel,
    type: 'PieSeries',
    bulletsContainer,
    labels: {
      disabled: true
    },
    slices: {
      template: {
        tooltipText: `{value.percent.formatNumber('#.0')}%`,
        type: 'Slice',
        propertyFields: {
          fill: 'color'
        },
        tooltip: {
          type: 'Tooltip',
          stroke: color.labelColor,
          label: {
            fontSize: 12,
            fill: color.labelColor
          }
        },
        states: {
          hover: {
            properties: {
              scale: 1
            }
          },
          active: {
            properties: {
              shiftRadius: 0
            }
          }
        }
      }
    },
    dataFields: {
      value: series,
      category: xKey
    },
    hiddenState: {
      transitionDuration: 500
    },
    defaultState: {
      transitionDuration: 500
    }
  }

  if (disabledAnimations) {
    delete config.hiddenState
    delete config.defaultState
  }

  return config
}

const getColumnSeries = ({ series, otherConfig }) => {
  const { color, sync, xKey, legendLabel, disabledAnimations } = otherConfig

  const config = {
    name: isEmpty(legendLabel) ? series : legendLabel,
    fill: color && color[series],
    type: 'ColumnSeries',
    stacked: false,
    yAxis: !sync ? 'CommonYAxis' : `ColumnSeries`,
    dataFields: {
      valueY: series,
      categoryX: xKey
    },
    hiddenState: {
      transitionDuration: 500
    },
    defaultState: {
      transitionDuration: 500
    },
    columns: {
      template: {
        width: '100%',
        type: 'Column',
        strokeOpacity: 0
      }
    }
  }

  if (disabledAnimations) {
    delete config.defaultState
    delete config.hiddenState
  }

  return config
}

const getGroupedSeries = ({ series, otherConfig }) => {
  const { legendLabel, disabledAnimations } = otherConfig
  return series.map((serie, i) => ({
    ...getColumnSeries({
      series: serie,
      otherConfig: { disabledAnimations, ...otherConfig }
    }),
    name: isEmpty(legendLabel) ? serie : legendLabel[i]
  }))
}

const getBullets = (otherBullets) => {
  const defaultBullet = [
    {
      width: 7,
      height: 7,
      type: 'Circle'
    }
  ]
  if (otherBullets) return [...defaultBullet, ...otherBullets]
  return defaultBullet
}

const getLineSeries = ({ series, otherConfig }) => {
  const {
    color,
    sync,
    xKey,
    linePositionX,
    legendLabel,
    otherBullets,
    disabledAnimations
  } = otherConfig
  if (isEmpty(series)) return []
  return series.map((serie, i) => {
    const config = {
      name: isEmpty(legendLabel) ? serie : legendLabel[i],
      zIndex: 100,
      type: 'LineSeries',
      fill: color && color[serie],
      stroke: color && color[serie],
      yAxis: !sync ? 'CommonYAxis' : `LineSeries`,
      strokeWidth: 2,
      hiddenState: {
        transitionDuration: 250
      },
      defaultState: {
        transitionDuration: 250
      },
      dataItems: {
        locations: {
          categoryX: linePositionX
        }
      },
      dataFields: {
        valueY: serie,
        categoryX: xKey
      },
      bullets: getBullets(otherBullets)
    }

    if (disabledAnimations) {
      delete config.hiddenState
      delete config.defaultState
    }

    return config
  })
}

export {
  getColumnSeries,
  getStackedSeries,
  getGroupedSeries,
  getLineSeries,
  getPieSeries
}

// Some style in index.scss (.amcharts-amexport-menu)
export const defaultExportMenu = {
  menu: {
    align: 'right',
    verticalAlign: 'top',
    items: [
      {
        icon: 'data:image/svg+xml;charset=utf8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%20width%3D%2211px%22%20height%3D%2214px%22%3E%3Cpath%20d%3D%22M3%2C0%20L8%2C0%20L8%2C5%20L11%2C5%20L5.5%2C10%20L0%2C5%20L3%2C5%20L03%2C0%22%20fill%3D%22%23888%22%2F%3E%3Crect%20x%3D%220%22%20y%3D%2212%22%20fill%3D%22%23888%22%20width%3D%2211%22%20height%3D%222%22%2F%3E%3C%2Fsvg%3E',
        menu: [
          {
            label: 'Image',
            menu: [
              { type: 'png', label: 'PNG' },
              { type: 'jpg', label: 'JPG' },
              { type: 'svg', label: 'SVG' },
              { type: 'pdf', label: 'PDF' }
            ]
          },
          {
            label: 'Data',
            menu: [
              { type: 'csv', label: 'CSV' },
              { type: 'xlsx', label: 'XLSX' }
            ]
          }
        ]
      }
    ]
  }
}
